import { NavLink, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  FileText, 
  BarChart3, 
  User,
  Brain,
  Trophy,
  CreditCard,
  Calendar,
  X,
  ClipboardList,
  LogOut,
  Gift
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const navigation = [
  { 
    name: 'Dashboard', 
    href: '/dashboard', 
    icon: LayoutDashboard,
    description: 'Overview & quick actions'
  },
  { 
    name: 'Mock Tests', 
    href: '/tests', 
    icon: FileText,
    description: 'Full-length practice tests'
  },
  { 
    name: 'Smart Practice', 
    href: '/practice', 
    icon: Brain,
    description: 'AI-powered topic practice'
  },
  { 
    name: 'Analytics', 
    href: '/analytics', 
    icon: BarChart3,
    description: 'Performance insights'
  },
  { 
    name: 'Progress', 
    href: '/progress', 
    icon: Trophy,
    description: 'Achievements & streaks'
  },
  { 
    name: 'Study Plan', 
    href: '/study-plan', 
    icon: Calendar,
    description: 'Personalized schedule'
  },
  { 
    name: 'Subscription', 
    href: '/subscription', 
    icon: CreditCard,
    description: 'Manage your plan'
  },
  { 
    name: 'Order History', 
    href: '/order-history', 
    icon: ClipboardList,
    description: 'View payment orders'
  },
  { 
    name: 'Refer & Earn', 
    href: '/referral', 
    icon: Gift,
    description: 'Invite friends, earn rewards'
  },
  { 
    name: 'Profile', 
    href: '/profile', 
    icon: User,
    description: 'Account settings'
  },
];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleNavClick = () => {
    if (window.innerWidth < 768) {
      onClose();
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed md:sticky top-0 left-0 z-50 md:z-0
        w-72 bg-white shadow-soft border-r border-secondary-200 
        h-screen overflow-y-auto
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>
        {/* Mobile Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-lg hover:bg-secondary-100 md:hidden touch-target-sm"
          aria-label="Close menu"
        >
          <X className="h-5 w-5 text-secondary-600" />
        </button>

        {/* Navigation */}
        <nav className="mt-6 px-4 pb-6">
          <ul className="space-y-2">
            {navigation.map((item) => (
              <li key={item.name}>
                <NavLink
                  to={item.href}
                  onClick={handleNavClick}
                  className={({ isActive }) =>
                    `nav-link group ${isActive ? 'nav-link-active' : 'nav-link-inactive'}`
                  }
                >
                  <div className="flex items-center flex-1">
                    <div className={`mr-4 p-2 rounded-lg transition-all duration-200 group-hover:scale-110`}>
                      <item.icon className="h-5 w-5 flex-shrink-0" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm">{item.name}</div>
                      <div className="text-xs text-secondary-500 group-hover:text-secondary-600 transition-colors">
                        {item.description}
                      </div>
                    </div>
                  </div>
                </NavLink>
              </li>
            ))}
          </ul>

          {/* Logout */}
          <div className="mt-4 pt-4 border-t border-secondary-200">
            <button
              onClick={handleLogout}
              className="nav-link nav-link-inactive group w-full text-left"
            >
              <div className="flex items-center flex-1">
                <div className="mr-4 p-2 rounded-lg transition-all duration-200 group-hover:scale-110">
                  <LogOut className="h-5 w-5 flex-shrink-0" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">Logout</div>
                  <div className="text-xs text-secondary-500 group-hover:text-secondary-600 transition-colors">
                    Sign out of your account
                  </div>
                </div>
              </div>
            </button>
          </div>
        </nav>
      </div>
    </>
  );
}